import Button from "components/Button";
import clsx from "clsx";
import Modal from "components/Modal";
import { makeDepositAPI } from "services/api/haulpay";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import { useSelector } from "react-redux";
import { selectUserDetails } from "store/user/selector";
import { FormProvider, useForm } from "react-hook-form";
import Icon from "@mdi/react";
import { mdiLoading } from "@mdi/js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import DollarInput from "components/DollarInput";

interface IProps {
  open: boolean;
  closeHandler: () => void;
}

export default function DepositMoneyWallet({ open, closeHandler }: IProps) {
  const userDetails = useSelector(selectUserDetails);
  const queryClient = useQueryClient();

  const methods = useForm({
    defaultValues: {
      deposit_amount: 0
    },
    mode: "onSubmit"
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset
  } = methods;

  const depositAmount = watch("deposit_amount");

  // useMutation for handling the deposit action
  const mutation = useMutation(
    (formData: { company_id: string; deposit_type: string; deposit_amount: number }) =>
      makeDepositAPI(formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["pendingTransactions"]);
        closeHandler();
        showSuccessMessage("Deposit has been created successfully.");
        reset();
      },
      onError: () => {
        showErrorMessage("Failed to create deposit.");
      }
    }
  );

  const submitDeposit = async (data: { deposit_amount: number }) => {
    const formData = {
      company_id: userDetails.factoring_id as string,
      deposit_type: "self_finance_debit",
      deposit_amount: data.deposit_amount
    };

    await mutation.mutateAsync(formData);
  };

  return (
    <Modal
      isOpen={open}
      closeModal={closeHandler}
      wrapperClass="!my-auto desktop:!min-h-fit text-sm"
      dialogClass="!bg-foreground !p-4 !border !border-card !overflow-y-auto gap-2.5"
      closeClass="!top-24 !right-6 !bg-transparent !text-text">
      <span className="text-xl font-bold text-primary">Make a Deposit</span>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submitDeposit)}>
          <div className="mt-4 mb-2">
            <DollarInput
              name="deposit_amount"
              label="&nbsp;Amount"
              inputClass="!border !border-subtitle"
              rules={{
                required: "Amount is required",
                min: { value: 0.01, message: "Amount must be greater than $0" },
                max: { value: 10000, message: "Amount cannot be greater than 10,000.00" }
              }}
            />
            {errors.deposit_amount && (
              <span className="text-sm text-red-500">{errors.deposit_amount.message}</span>
            )}
          </div>
          <div className="flex gap-4 justify-between *:text-sm *:rounded-full *:h-9 pt-4">
            <Button variant="outline" onClick={closeHandler}>
              Cancel
            </Button>
            <Button
              className={clsx(
                !depositAmount
                  ? "!bg-subtitle !ring-subtitle cursor-not-allowed"
                  : "bg-gradient-to-r from-primary to-secondary"
              )}
              disabled={isSubmitting}
              type="submit">
              {isSubmitting ? <Icon path={mdiLoading} size={1} spin={1} /> : "Deposit"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}
